import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { api, onSessionRejected, refreshSession } from '../api/client';
import {
  clearSession, describeDevice, loadSession, rememberDeviceId, rememberedDeviceId, saveSession,
  type StoredSession,
} from '../api/tokens';
import type { Chat, Message, PendingMessage, Person, TokenResponse } from '../api/types';
import { Socket, type Frame, type SocketStatus } from '../ws/Socket';
import { clearDeliveredNotifications, registerPush } from '../push/registerPush';
import { notificationsEnabled } from '../push/notificationSwitch';
import { noteSessionEnd } from '../api/sessionEnd';
import { registerWebPush } from '../push/registerWebPush';

interface AppState {
  session: StoredSession | null;
  status: SocketStatus;
  chats: Chat[];
  /** Те, с кем уже есть переписка. Общего списка участников нет by design. */
  contacts: Person[];
  onlineUserIds: Set<string>;
  activeChatId: string | null;
  messages: Message[];
  pending: PendingMessage[];
  typingUserIds: string[];
  historyExhausted: boolean;
  loadingHistory: boolean;

  register: (input: RegisterInput) => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  selectChat: (chatId: string | null) => void;
  startDirectChat: (userId: string) => Promise<string>;
  searchPeople: (query: string) => Promise<Person[]>;
  createGroup: (title: string, memberIds: string[]) => Promise<string>;
  addMembers: (chatId: string, memberIds: string[]) => Promise<void>;
  leaveChat: (chatId: string) => Promise<void>;
  /** Сообщение, на которое отвечаем. null — обычная отправка. */
  replyTo: Message | null;
  setReplyTo: (message: Message | null) => void;
  sendMessage: (body: string) => void;
  toggleReaction: (messageId: string, emoji: string) => void;
  retryPending: (clientMsgId: string) => void;
  loadOlder: () => Promise<void>;
  notifyTyping: () => void;
  deleteMessage: (messageId: string) => void;
}

export interface RegisterInput {
  inviteCode: string;
  username: string;
  displayName: string;
  password: string;
}

const Context = createContext<AppState | null>(null);

export function useApp(): AppState {
  const value = useContext(Context);
  if (!value) throw new Error('useApp вне AppProvider');
  return value;
}

const TYPING_TTL_MS = 4000;
const HISTORY_PAGE = 40;

export function AppProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<StoredSession | null>(() => loadSession());
  const [status, setStatus] = useState<SocketStatus>('idle');
  const [chats, setChats] = useState<Chat[]>([]);
  const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(new Set());
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [messagesByChat, setMessagesByChat] = useState<Record<string, Message[]>>({});
  const [pendingByChat, setPendingByChat] = useState<Record<string, PendingMessage[]>>({});
  const [typingByChat, setTypingByChat] = useState<Record<string, Record<string, number>>>({});
  const [exhaustedChats, setExhaustedChats] = useState<Set<string>>(new Set());
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [replyTo, setReplyTo] = useState<Message | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const activeChatRef = useRef<string | null>(null);
  const bodyByClientMsgId = useRef<Map<string, string>>(new Map());
  const lastTypingSentAt = useRef(0);
  /** Ссылка нужна, потому что обработчик кадров объявлен раньше самой функции. */
  const refreshChatsRef = useRef<(() => Promise<void>) | null>(null);
  /** Для обработчиков окна: они живут дольше одного отрисованного состояния. */
  const chatsRef = useRef<Chat[]>([]);
  /**
   * Открытие чата по нажатию на уведомление.
   *
   * Через ссылку, потому что подписка на уведомления оформляется раньше, чем
   * объявлена сама функция. Раньше здесь просто менялся текущий чат — история
   * при этом не загружалась и прочтение не отмечалось.
   */
  const openChatRef = useRef<((chatId: string) => void) | null>(null);

  activeChatRef.current = activeChatId;
  chatsRef.current = chats;

  // ── обработка кадров ──────────────────────────────────────────────────────

  const upsertMessages = useCallback((chatId: string, incoming: Message[]) => {
    if (incoming.length === 0) return;
    setMessagesByChat((previous) => {
      const current = previous[chatId] ?? [];
      const byId = new Map(current.map((m) => [m.id, m]));
      for (const message of incoming) {
        byId.set(message.id, message);
      }
      const merged = [...byId.values()].sort((a, b) => a.seq - b.seq);
      return { ...previous, [chatId]: merged };
    });
  }, []);

  const handleFrame = useCallback((frame: Frame) => {
    switch (frame.t) {
      case 'msg.new': {
        const message = frame.d as Message;
        upsertMessages(message.chatId, [message]);

        setChats((previous) => {
          // Сообщение из чата, которого нет в списке: карточка чата могла
          // не дойти, если человек был не на связи в момент создания переписки.
          // Без этого сообщение молча оседает в памяти и нигде не показывается.
          if (!previous.some((chat) => chat.id === message.chatId)) {
            void refreshChatsRef.current?.();
            return previous;
          }
          return previous.map((chat) => {
            if (chat.id !== message.chatId) return chat;
            const isActive = activeChatRef.current === message.chatId
              && document.visibilityState === 'visible';
            const own = message.senderId === session?.user.id;
            return {
              ...chat,
              lastSeq: Math.max(chat.lastSeq, message.seq),
              lastMessage: message,
              lastReadSeq: isActive || own ? message.seq : chat.lastReadSeq,
              unread: isActive || own ? 0 : chat.unread + 1,
            };
          });
        });
        // Прочитанным считаем только если чат открыт И окно на виду.
        // Свёрнутое приложение с открытым чатом иначе рапортовало бы «прочитано»
        // за человека, который ничего не видел.
        if (activeChatRef.current === message.chatId
            && document.visibilityState === 'visible'
            && message.senderId !== session?.user.id) {
          socketRef.current?.send({ t: 'read', d: { chatId: message.chatId, seq: message.seq } });
        }
        break;
      }
      case 'msg.ack': {
        const { clientMsgId, messageId, seq, createdAt } = frame.d as {
          clientMsgId: string; messageId: string; seq: number; createdAt: string;
        };
        const body = bodyByClientMsgId.current.get(clientMsgId);
        bodyByClientMsgId.current.delete(clientMsgId);

        // Заменяем локальное сообщение подтверждённым. Тело берём своё:
        // сервер в подтверждении его не дублирует.
        setPendingByChat((previous) => {
          const next: Record<string, PendingMessage[]> = {};
          for (const [chatId, list] of Object.entries(previous)) {
            const filtered = list.filter((p) => p.clientMsgId !== clientMsgId);
            const found = list.find((p) => p.clientMsgId === clientMsgId);
            if (found) {
              upsertMessages(chatId, [{
                id: messageId, chatId, seq, senderId: session?.user.id ?? null,
                kind: 'TEXT', body: body ?? found.body,
                replyToId: found.replyToId, replyToAuthor: null, replyToText: null,
                reactions: [],
                payload: null,
                createdAt, editedAt: null, deleted: false,
              }]);
            }
            next[chatId] = filtered;
          }
          return next;
        });
        break;
      }
      case 'msg.updated':
      case 'msg.deleted': {
        const message = frame.d as Message;
        upsertMessages(message.chatId, [message]);
        break;
      }
      case 'typing': {
        const { chatId, userId } = frame.d as { chatId: string; userId: string };
        setTypingByChat((previous) => ({
          ...previous,
          [chatId]: { ...(previous[chatId] ?? {}), [userId]: Date.now() },
        }));
        break;
      }
      case 'read': {
        const { chatId, userId, seq } = frame.d as { chatId: string; userId: string; seq: number };
        setChats((previous) => previous.map((chat) => {
          if (chat.id !== chatId) return chat;
          const members = chat.members.map((member) => member.userId === userId
            ? { ...member, lastReadSeq: Math.max(member.lastReadSeq, seq) }
            : member);
          return userId === session?.user.id
            ? { ...chat, members, lastReadSeq: Math.max(chat.lastReadSeq, seq),
                unread: Math.max(0, chat.lastSeq - seq) }
            : { ...chat, members };
        }));
        break;
      }
      case 'presence': {
        const { userId, online } = frame.d as { userId: string; online: boolean };
        setOnlineUserIds((previous) => {
          const next = new Set(previous);
          if (online) next.add(userId); else next.delete(userId);
          return next;
        });
        break;
      }
      case 'sync.result': {
        const { chatId, messages } = frame.d as { chatId: string; messages: Message[] };
        upsertMessages(chatId, messages);
        break;
      }
      case 'chat.updated': {
        const chat = frame.d as Chat;
        // Кадр приходит и тем, кого только что добавили в группу, — тогда чата
        // ещё нет в списке и его нужно добавить, а не обновить
        setChats((previous) => previous.some((c) => c.id === chat.id)
          ? previous.map((c) => c.id === chat.id
              ? { ...chat, unread: c.unread, lastReadSeq: c.lastReadSeq }
              : c)
          : [chat, ...previous]);
        break;
      }
      default:
        break;
    }
  }, [session?.user.id, upsertMessages]);

  // ── загрузка списков ──────────────────────────────────────────────────────

  const refreshChats = useCallback(async () => {
    try {
      setChats(await api.chats());
    } catch {
      // Сессию здесь не трогаем. Раньше любой 401 означал выход из аккаунта,
      // в том числе 401 после неудачного из-за обрыва связи обновления
      // токена — и человека выбрасывало на ровном месте. Решение о том,
      // что сессии больше нет, принимает только обновление токена.
    }
  }, []);

  refreshChatsRef.current = refreshChats;

  /** После переподключения догружаем всё, что пропустили в каждом известном чате. */
  const resync = useCallback(() => {
    void refreshChats();
    const socket = socketRef.current;
    if (!socket) return;
    setMessagesByChat((current) => {
      for (const [chatId, list] of Object.entries(current)) {
        const lastSeq = list.length > 0 ? (list[list.length - 1]?.seq ?? 0) : 0;
        socket.send({ t: 'sync', d: { chatId, sinceSeq: lastSeq } });
      }
      return current;
    });
  }, [refreshChats]);

  // ── жизненный цикл сокета ────────────────────────────────────────────────

  useEffect(() => {
    if (!session) {
      socketRef.current?.close();
      socketRef.current = null;
      setStatus('idle');
      return;
    }

    const socket = new Socket({
      getAccessToken: () => loadSession()?.accessToken ?? null,
      refreshAccessToken: async () => {
        const outcome = await refreshSession();
        if (outcome.status === 'ok') {
          return outcome.token;
        }
        // Из аккаунта отсюда не выходим ни при каком ответе.
        //
        // Диагностика показала, что выход делал именно этот путь: обновление
        // вернуло отказ, хотя сервер за те же сутки не отказал ни разу —
        // значит, запрос до него не дошёл, а отказ родился на устройстве.
        // Сессию заканчивает только прямой отказ сервера, и только в том
        // месте, которое с сервером разговаривает.
        throw new Error('обновить токен не удалось');
      },
      onFrame: handleFrame,
      onStatus: (next) => {
        // «Нужен вход» показывается в строке состояния, но сессию не трогает:
        // соединение не знает, жива ли она, — знает только сервер
        setStatus(next);
      },
      onAuthenticated: resync,
    });
    socketRef.current = socket;
    socket.connect();

    // Подписка на уведомления привязана к устройству этой сессии: сервер
    // отправляет пуши на устройство, а не «пользователю вообще»
    // Два канала уведомлений: в приложении — FCM, в браузере — Web Push.
    // Каждая из функций сама проверяет, её ли это случай.
    //
    // Выключатель — на устройство: у кого есть и приложение, и браузер,
    // о каждом сообщении приходило по два уведомления.
    if (notificationsEnabled()) {
      void registerPush(session.deviceId, (chatId) => openChatRef.current?.(chatId));
      void registerWebPush(session.deviceId, (chatId) => openChatRef.current?.(chatId));
    }

    // Сообщаем серверу оба перехода, а не только возврат: уведомление нужно
    // именно когда окно свернули, и об этом сервер должен узнать сразу
    const onVisibility = () => {
      const visible = document.visibilityState === 'visible';
      socket.reportActivity(visible);
      if (visible) {
        socket.wakeUp();
        // Разрешение могли выдать уже после загрузки страницы — через значок
        // замка в адресной строке. Повторная попытка идемпотентна: если подписка
        // есть, браузер вернёт существующую.
        if (notificationsEnabled()) {
          void registerWebPush(session.deviceId, (chatId) => openChatRef.current?.(chatId));
        }
      }
    };
    const onOnline = () => socket.wakeUp();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      socket.close();
      socketRef.current = null;
    };
  }, [session, handleFrame, resync]);

  // Устаревшие индикаторы «печатает» гаснут сами
  useEffect(() => {
    const timer = window.setInterval(() => {
      setTypingByChat((previous) => {
        const now = Date.now();
        let changed = false;
        const next: Record<string, Record<string, number>> = {};
        for (const [chatId, users] of Object.entries(previous)) {
          const fresh: Record<string, number> = {};
          for (const [userId, at] of Object.entries(users)) {
            if (now - at < TYPING_TTL_MS) fresh[userId] = at; else changed = true;
          }
          next[chatId] = fresh;
        }
        return changed ? next : previous;
      });
    }, 1500);
    return () => clearInterval(timer);
  }, []);

  // ── действия ──────────────────────────────────────────────────────────────

  const applyTokens = useCallback((tokens: TokenResponse) => {
    rememberDeviceId(tokens.deviceId);
    setSession(saveSession(tokens));
  }, []);

  const register = useCallback(async (input: RegisterInput) => {
    const tokens = await api.register({
      ...input,
      platform: 'WEB',
      deviceName: describeDevice(),
    });
    applyTokens(tokens);
  }, [applyTokens]);

  const login = useCallback(async (username: string, password: string) => {
    const tokens = await api.login({
      username, password,
      platform: 'WEB',
      deviceName: describeDevice(),
      deviceId: rememberedDeviceId(),
    });
    applyTokens(tokens);
  }, [applyTokens]);

  const logout = useCallback(async () => {
    noteSessionEnd('вы вышли сами');
    try {
      await api.logout();
    } catch {
      // Даже если отзыв не удался, локально выходим
    }
    clearSession();
    setSession(null);
    setChats([]);
    setMessagesByChat({});
    setPendingByChat({});
    setActiveChatId(null);
  }, []);

  const loadHistory = useCallback(async (chatId: string, before?: number) => {
    setLoadingHistory(true);
    try {
      const page = await api.history(chatId, before, HISTORY_PAGE);
      if (page.length < HISTORY_PAGE) {
        setExhaustedChats((previous) => new Set(previous).add(chatId));
      }
      upsertMessages(chatId, page);
    } finally {
      setLoadingHistory(false);
    }
  }, [upsertMessages]);

  const selectChat = useCallback((chatId: string | null) => {
    setActiveChatId(chatId);
    if (!chatId) return;

    const known = messagesByChat[chatId];
    if (!known || known.length === 0) {
      void loadHistory(chatId);
    }
    // Открыли чат — значит прочитали
    const chat = chats.find((c) => c.id === chatId);
    if (chat && chat.lastSeq > chat.lastReadSeq) {
      socketRef.current?.send({ t: 'read', d: { chatId, seq: chat.lastSeq } });
      setChats((previous) => previous.map((c) =>
        c.id === chatId ? { ...c, lastReadSeq: c.lastSeq, unread: 0 } : c));
    }
  }, [chats, loadHistory, messagesByChat]);

  openChatRef.current = selectChat;

  /**
   * Значок с числом на приложении держится, пока уведомление лежит в шторке.
   * Убираем показанные, когда непрочитанного не осталось: иначе значок обещает
   * сообщение, которого нет.
   */
  useEffect(() => {
    if (chats.length > 0 && !chats.some((chat) => chat.unread > 0)) {
      void clearDeliveredNotifications();
    }
  }, [chats]);

  // Единственный путь на экран входа, кроме нажатия «выйти»: сервер прямо
  // отказал в обновлении токена. Подписываемся один раз на всё время жизни.
  useEffect(() => onSessionRejected(() => setSession(null)), []);

  /**
   * Вернулись к окну — значит прочитали то, что открыто.
   *
   * Без этого отправитель мог долго видеть одну галочку: отметка уходила
   * только при получении сообщения на видимом экране или при открытии чата.
   * Если чат уже открыт, а окно свёрнуто, возвращение к нему не отправляло
   * ничего, и прочтение подтверждалось лишь со следующим сообщением.
   */
  useEffect(() => {
    const markVisibleChatRead = () => {
      if (document.visibilityState !== 'visible') return;
      const chatId = activeChatRef.current;
      if (!chatId) return;
      const chat = chatsRef.current.find((c) => c.id === chatId);
      if (!chat || chat.lastSeq <= chat.lastReadSeq) return;

      socketRef.current?.send({ t: 'read', d: { chatId, seq: chat.lastSeq } });
      setChats((previous) => previous.map((c) =>
        c.id === chatId ? { ...c, lastReadSeq: c.lastSeq, unread: 0 } : c));
    };

    // Событие видимости приходит и при сворачивании приложения на телефоне,
    // focus — при возврате к окну на компьютере без смены видимости
    document.addEventListener('visibilitychange', markVisibleChatRead);
    window.addEventListener('focus', markVisibleChatRead);
    return () => {
      document.removeEventListener('visibilitychange', markVisibleChatRead);
      window.removeEventListener('focus', markVisibleChatRead);
    };
  }, []);

  const startDirectChat = useCallback(async (userId: string) => {
    const chat = await api.createDirect(userId);
    setChats((previous) => previous.some((c) => c.id === chat.id) ? previous : [chat, ...previous]);
    setActiveChatId(chat.id);
    void loadHistory(chat.id);
    return chat.id;
  }, [loadHistory]);

  const searchPeople = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (trimmed.length < 2) return [];
    return api.searchUsers(trimmed);
  }, []);

  const createGroup = useCallback(async (title: string, memberIds: string[]) => {
    const chat = await api.createGroup(title, memberIds);
    setChats((previous) => previous.some((c) => c.id === chat.id) ? previous : [chat, ...previous]);
    setActiveChatId(chat.id);
    void loadHistory(chat.id);
    return chat.id;
  }, [loadHistory]);

  const addMembers = useCallback(async (chatId: string, memberIds: string[]) => {
    const chat = await api.addMembers(chatId, memberIds);
    setChats((previous) => previous.map((c) => c.id === chat.id ? { ...c, ...chat } : c));
  }, []);

  const leaveChat = useCallback(async (chatId: string) => {
    await api.leaveChat(chatId);
    setChats((previous) => previous.filter((c) => c.id !== chatId));
    setActiveChatId((current) => current === chatId ? null : current);
    setMessagesByChat((previous) => {
      const next = { ...previous };
      delete next[chatId];
      return next;
    });
  }, []);

  const sendMessage = useCallback((body: string) => {
    const chatId = activeChatRef.current;
    const text = body.trim();
    if (!chatId || !text) return;

    const clientMsgId = crypto.randomUUID();
    bodyByClientMsgId.current.set(clientMsgId, text);
    const replyToId = replyTo?.id ?? null;
    setReplyTo(null);

    // Показываем сразу: ждать ответа сервера, чтобы нарисовать своё же
    // сообщение — заметная на глаз задержка
    setPendingByChat((previous) => ({
      ...previous,
      [chatId]: [...(previous[chatId] ?? []), {
        clientMsgId, chatId, body: text, replyToId,
        createdAt: new Date().toISOString(),
        status: 'sending',
      }],
    }));

    socketRef.current?.send({ t: 'msg.send', d: { chatId, clientMsgId, body: text, replyToId } });
  }, [replyTo]);

  const toggleReaction = useCallback((messageId: string, emoji: string) => {
    socketRef.current?.send({ t: 'reaction.set', d: { messageId, emoji } });
  }, []);

  const retryPending = useCallback((clientMsgId: string) => {
    const chatId = activeChatRef.current;
    if (!chatId) return;
    const item = pendingByChat[chatId]?.find((p) => p.clientMsgId === clientMsgId);
    if (!item) return;
    // Тот же clientMsgId: сервер вернёт уже сохранённое сообщение, а не создаст дубль
    socketRef.current?.send({ t: 'msg.send', d: { chatId, clientMsgId, body: item.body, replyToId: item.replyToId } });
  }, [pendingByChat]);

  const loadOlder = useCallback(async () => {
    const chatId = activeChatRef.current;
    if (!chatId || exhaustedChats.has(chatId) || loadingHistory) return;
    const list = messagesByChat[chatId] ?? [];
    const oldest = list[0]?.seq;
    await loadHistory(chatId, oldest);
  }, [exhaustedChats, loadHistory, loadingHistory, messagesByChat]);

  const notifyTyping = useCallback(() => {
    const chatId = activeChatRef.current;
    if (!chatId) return;
    // Не чаще раза в две секунды: событие информационное, спамить им незачем
    const now = Date.now();
    if (now - lastTypingSentAt.current < 2000) return;
    lastTypingSentAt.current = now;
    socketRef.current?.send({ t: 'typing', d: { chatId } });
  }, []);

  const deleteMessage = useCallback((messageId: string) => {
    socketRef.current?.send({ t: 'msg.delete', d: { messageId } });
  }, []);

  /**
   * Адресная книга собирается из участников уже существующих чатов: список всех
   * пользователей сервер не отдаёт, а знакомых людей выбирать удобно.
   */
  const contacts = useMemo<Person[]>(() => {
    const byId = new Map<string, Person>();
    for (const chat of chats) {
      for (const member of chat.members) {
        if (member.userId === session?.user.id) continue;
        if (!byId.has(member.userId)) {
          byId.set(member.userId, {
            id: member.userId,
            username: member.username ?? '',
            displayName: member.displayName,
            lastSeenAt: null,
          });
        }
      }
    }
    return [...byId.values()]
      .sort((a, b) => a.displayName.localeCompare(b.displayName, 'ru'));
  }, [chats, session?.user.id]);

  const value = useMemo<AppState>(() => {
    const chatId = activeChatId;
    const typing = chatId ? typingByChat[chatId] ?? {} : {};
    return {
      session, status, chats, contacts, onlineUserIds, activeChatId,
      messages: chatId ? messagesByChat[chatId] ?? [] : [],
      pending: chatId ? pendingByChat[chatId] ?? [] : [],
      typingUserIds: Object.keys(typing).filter((id) => id !== session?.user.id),
      historyExhausted: chatId ? exhaustedChats.has(chatId) : true,
      loadingHistory,
      replyTo, setReplyTo,
      register, login, logout, selectChat, startDirectChat, searchPeople, createGroup,
      addMembers, leaveChat, sendMessage, toggleReaction, retryPending, loadOlder,
      notifyTyping, deleteMessage,
    };
  }, [session, status, chats, contacts, onlineUserIds, activeChatId, messagesByChat,
      pendingByChat, typingByChat, exhaustedChats, loadingHistory, replyTo,
      register, login, logout, selectChat, startDirectChat, searchPeople, createGroup,
      addMembers, leaveChat, sendMessage, toggleReaction, retryPending, loadOlder,
      notifyTyping, deleteMessage]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
}
